/**
 * Shared shapes for the code-execution container's file listing, used by both
 * /api/chat/container-files and the panel that renders it.
 */

/** One file as reported by the code-execution container. */
export type ContainerFile = {
  path: string;
  size?: number;
  modifiedAt?: string | null;
};

/**
 * Container lifecycle, surfaced verbatim so the panel can never imply files
 * exist after the sandbox has gone.
 *
 * - `none` — this chat has never run code, or its container fell outside the
 *   idle window and was dropped.
 * - `active` — a listing just succeeded against it.
 * - `checkpointed` — stored and not expired, but not yet confirmed live this
 *   request. Anthropic restores on reference, so a refresh usually revives it.
 * - `expired` — Anthropic rejected the id; the sandbox and its files are gone.
 */
export type ContainerStatus = "active" | "checkpointed" | "expired" | "none";

export type ContainerListResponse = {
  provider?: "anthropic" | "openai";
  status: ContainerStatus;
  files: ContainerFile[];
  /** When the listing was read off the container. Null when nothing was read. */
  listedAt: string | null;
  /** Earliest provider/app deadline after which this container is not reused. */
  usableUntil: string | null;
};

export type ContainerReadResponse = {
  path: string;
  content: string;
  usableUntil?: string;
};

/**
 * Root the sandbox exposes to users. Anthropic mounts container_upload files at
 * /files/input/<hash>/<name> and anything the model saves for download at
 * /files/output/<hash>/<name>. It is walked in full (bar the export staging),
 * while the working directory `/` is walked one level deep — that combination
 * is what keeps the panel free of OS noise and of /tmp, which the model is
 * told to use for scratch and which is full of uv locks and node caches.
 */
export const CONTAINER_FILES_ROOT = "/files";

/**
 * Where the model is told to keep everything it makes for the user. Verified
 * against a live container: `/files` is writable, a path under it survives
 * across turns, and overwriting keeps one copy at one path.
 *
 * A dedicated directory is necessary because neither of the container's own
 * locations works for this:
 *
 * - Left to itself the model writes to `/tmp` — the listing root deliberately
 *   excludes it (uv locks, node caches), and nothing there is user-facing.
 * - Files the container auto-exports land in `/files/output/<hash>/<name>`, but
 *   that copy is a snapshot taken when the file was created: editing the
 *   original on a later turn leaves the exported copy at its old size and
 *   mtime, so the panel would offer a stale download under an opaque hash.
 *
 * Sibling of `/files/output` rather than inside it, so our stable paths never
 * sit among the hash directories the container manages itself.
 */
export const CONTAINER_WORKSPACE_DIR = `${CONTAINER_FILES_ROOT}/workspace`;

/** Where container_upload attachments arrive, as `<name>` under a hash dir. */
export const CONTAINER_INPUT_DIR = `${CONTAINER_FILES_ROOT}/input`;

/**
 * The container's own export staging — one opaque hash directory per code
 * execution invocation. Deliberately never listed: the copies there are
 * snapshots taken when a file was created, so once the model edits the
 * original they are wrong, and showing them beside the real file would put the
 * same name in the tree twice with no way to tell which download is current.
 */
export const CONTAINER_OUTPUT_DIR = `${CONTAINER_FILES_ROOT}/output`;

/** Container-managed metadata that sits at the working directory. Not the user's. */
const CONTAINER_INFO_FILE = "/container_info.json";

/**
 * Whether a path belongs in the panel, and the single authority for it: the
 * find command, the parser reading its output, and the route that validates a
 * read request all defer here, so a path can never be listed but unreadable.
 *
 * Everything under /files qualifies apart from the export staging. Outside it
 * only files sitting directly at `/` do — that is the working directory, so a
 * relative write lands there, while anything deeper is OS noise (/etc, /usr)
 * or the scratch space the model is told to use (/tmp).
 */
export function isListedContainerPath(path: string): boolean {
  if (!path.startsWith("/") || path.includes("..")) return false;
  if (path.startsWith(`${CONTAINER_OUTPUT_DIR}/`)) return false;
  if (path.startsWith(`${CONTAINER_FILES_ROOT}/`)) return true;
  return /^\/[^/]+$/.test(path) && path !== CONTAINER_INFO_FILE;
}

/**
 * Strips the shared root so the panel's tree opens on `workspace/` and
 * `input/` rather than nesting everything under a redundant `files/`, and
 * shows a working-directory write as a bare filename at the top level.
 *
 * Two paths can only collapse to one display key in the single case of
 * `/name` alongside `/files/name`. The panel resolves a display key back to
 * the first matching file and builds its tree the same way, so the two always
 * agree on which file that is.
 */
export function containerDisplayPath(path: string): string {
  if (path.startsWith("/mnt/data/")) return path.slice("/mnt/data/".length);
  return path.startsWith(`${CONTAINER_FILES_ROOT}/`)
    ? path.slice(CONTAINER_FILES_ROOT.length + 1)
    : path.replace(/^\/+/, "");
}
